import { useState, useRef, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  ArrowLeft,
  Save,
  X,
  Bold,
  Italic,
  Underline,
  AlignLeft,
  AlignCenter,
  AlignRight,
  List,
  ListOrdered,
  Link as LinkIcon,
  Image as ImageIcon,
  Code,
  Pencil,
  Tag,
  User,
  Globe,
  FileText,
  Trash2,
  CheckCircle2,
} from "lucide-react";
import useBlogDetail from "../hooks/useBlogDetail";

export default function BlogDetailPage() {
  const navigate = useNavigate();
  const { id } = useParams();
  const {
    isNew,
    isViewMode,
    isLoading,
    isFetching,
    error,
    title,
    setTitle,
    content,
    setContent,
    status,
    setStatus,
    slug,
    setSlug,
    imageUrl,
    setImageUrl,
    category,
    setCategory,
    categories,
    tags,
    tagInput,
    setTagInput,
    seoTitle,
    setSeoTitle,
    seoDescription,
    setSeoDescription,
    author,
    setAuthor,
    computedSeoTitle,
    computedSeoDescription,
    wordCount,
    charCount,
    handleAddTag,
    handleRemoveTag,
    handleSave,
    handleCancel,
    handleToggleStatus,
  } = useBlogDetail();

  const [showSeoFields, setShowSeoFields] = useState(false);
  const editorRef = useRef(null);

  // Set initial content in the editor once loading is done
  useEffect(() => {
    if (editorRef.current && !isLoading && !isViewMode) {
      if (editorRef.current.innerHTML !== content && (!editorRef.current.innerHTML || editorRef.current.innerHTML === '<br>')) {
        editorRef.current.innerHTML = content || "";
      }
    }
  }, [isLoading, isViewMode, content]);

  const [activeFormats, setActiveFormats] = useState({});

  const updateActiveFormats = () => {
    try {
      setActiveFormats({
        bold: document.queryCommandState('bold'),
        italic: document.queryCommandState('italic'),
        underline: document.queryCommandState('underline'),
        justifyLeft: document.queryCommandState('justifyLeft'),
        justifyCenter: document.queryCommandState('justifyCenter'),
        justifyRight: document.queryCommandState('justifyRight'),
        insertUnorderedList: document.queryCommandState('insertUnorderedList'),
        insertOrderedList: document.queryCommandState('insertOrderedList'),
      });
    } catch (_) {}
  };

  useEffect(() => {
    document.addEventListener('selectionchange', updateActiveFormats);
    return () => document.removeEventListener('selectionchange', updateActiveFormats);
  }, []);

  const handleFormat = (command, value = null) => {
    // Focus the editor first so execCommand applies inside it
    if (editorRef.current) {
      editorRef.current.focus();
    }
    if (command === 'createLink') {
      const url = prompt('Enter link URL:');
      if (url) document.execCommand(command, false, url);
    } else if (command === 'insertImage') {
      const url = prompt('Enter image URL:');
      if (url) document.execCommand(command, false, url);
    } else if (command === 'insertCode') {
      const code = prompt('Enter code snippet:');
      if (code) {
        const escapedCode = code.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
        document.execCommand('insertHTML', false, `<pre class="bg-slate-800 text-slate-100 p-4 rounded-md overflow-x-auto text-sm my-4 font-mono"><code>${escapedCode}</code></pre><p><br></p>`);
      }
    } else {
      document.execCommand(command, false, value);
    }
    
    if (editorRef.current) {
      editorRef.current.focus();
      setContent(editorRef.current.innerHTML);
    }
    updateActiveFormats();
  };

  const categoryNameDisplay = (() => {
    const found = categories.find(
      (cat) => String(cat.id) === String(category) || cat.categoryName === category
    );
    return found ? found.categoryName : category;
  })();

  // Full-page loading spinner while fetching
  if (isFetching) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="flex flex-col items-center gap-3">
          <div className="w-10 h-10 border-4 border-teal-700 border-t-transparent rounded-full animate-spin" />
          <p className="text-sm text-slate-500 font-medium">Loading post...</p>
        </div>
      </div>
    );
  }

  // ── VIEW MODE ──────────────────────────────────────────────────────────────
  if (isViewMode) {
    return (
      <div className="p-4 max-w-[1400px] mx-auto space-y-6 pb-24 sm:pb-6">
        {error && (
          <div className="flex items-start gap-3 bg-red-50 border border-red-200 rounded-xl px-4 py-3">
            <span className="text-red-500 font-bold text-lg leading-none mt-0.5">!</span>
            <p className="text-sm text-red-700 font-medium">{error}</p>
          </div>
        )}

        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center pb-4 border-b border-slate-200 gap-4">
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={handleCancel}
              className="p-2 border border-slate-200 rounded-xl text-slate-500 hover:bg-slate-50 transition-all active:scale-95 flex items-center justify-center cursor-pointer"
              title="Back to Blogs"
            >
              <ArrowLeft size={16} />
            </button>
            <div>
              <h2 className="text-xl font-extrabold text-slate-800 tracking-tight line-clamp-1">
                {title || "Untitled Post"}
              </h2>
              <p className="text-xs text-slate-400 font-medium">View Mode — Read Only</p>
            </div>
          </div>
          <div className="hidden sm:flex items-center gap-2">
            {status === "published" ? (
              <button
                onClick={handleToggleStatus}
                disabled={isLoading}
                className="btn btn-sm flex items-center gap-1.5 bg-rose-50 border border-rose-200 text-rose-600 hover:bg-rose-100 rounded-xl px-3 py-1.5 text-xs font-semibold transition-all disabled:opacity-50 cursor-pointer"
                title="Unpublish this post"
              >
                {isLoading ? (
                  <div className="w-3.5 h-3.5 border-2 border-rose-400 border-t-transparent rounded-full animate-spin" />
                ) : (
                  <Trash2 size={14} />
                )}
                Delete
              </button>
            ) : (
              <button
                onClick={handleToggleStatus}
                disabled={isLoading}
                className="btn btn-sm flex items-center gap-1.5 bg-emerald-50 border border-emerald-200 text-emerald-700 hover:bg-emerald-100 rounded-xl px-3 py-1.5 text-xs font-semibold transition-all disabled:opacity-50 cursor-pointer"
                title="Publish this post"
              >
                {isLoading ? (
                  <div className="w-3.5 h-3.5 border-2 border-emerald-500 border-t-transparent rounded-full animate-spin" />
                ) : (
                  <CheckCircle2 size={14} />
                )}
                Publish
              </button>
            )}
            <button
              onClick={() => navigate(`/blogmanage/${id}?mode=edit`)}
              className="btn btn-p btn-sm flex items-center gap-1.5"
            >
              <Pencil size={14} />
              Edit Post
            </button>
          </div>
        </div>

        {/* Content grid */}
        <div className="grid grid-cols-1 xl:grid-cols-12 gap-6">
          {/* Main content */}
          <div className="xl:col-span-8 space-y-6">
            {/* Title */}
            <div className="bg-white p-6 rounded-xl border border-slate-200 shadow-sm">
              <p className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-2">Title</p>
              <h1 className="text-3xl font-bold tracking-tight text-slate-800">
                {title || <span className="text-slate-300">No title</span>}
              </h1>
            </div>

            {/* Body */}
            <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
              <div className="px-6 py-3 border-b border-slate-100 bg-slate-50 flex flex-col gap-1 sm:flex-row sm:items-center sm:justify-between">
                <p className="text-xs font-bold text-slate-400 uppercase tracking-wider">Content</p>
                <div className="flex items-center gap-4 text-slate-400 text-xs font-semibold">
                  <span>Words: {wordCount}</span>
                  <span>Characters: {charCount}</span>
                </div>
              </div>
              <div 
                className="p-6 min-h-[300px] text-base text-slate-700 leading-relaxed [&_ul]:list-disc [&_ul]:ml-4 [&_ol]:list-decimal [&_ol]:ml-4 [&_pre]:bg-slate-800 [&_pre]:text-slate-100 [&_pre]:p-4 [&_pre]:rounded-md [&_pre]:overflow-x-auto [&_pre]:my-4 [&_pre]:font-mono [&_a]:text-teal-600 [&_a]:underline"
                dangerouslySetInnerHTML={{ __html: content || '<span class="text-slate-300">No content</span>' }}
              />
            </div>
          </div>

          {/* Sidebar */}
          <div className="xl:col-span-4 space-y-6">
            {/* Post Settings */}
            <div className="bg-white p-6 rounded-xl border border-slate-200 shadow-sm space-y-4">
              <h3 className="text-base font-bold text-teal-900">Post Settings</h3>
              <div className="space-y-3">
                <div className="flex items-center gap-3">
                  <User size={14} className="text-slate-400 flex-shrink-0" />
                  <div>
                    <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Author</p>
                    <p className="text-sm font-medium text-slate-700">{author || "—"}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <FileText size={14} className="text-slate-400 flex-shrink-0" />
                  <div>
                    <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Status</p>
                    <span className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-bold border mt-0.5 ${
                      status === "published"
                        ? "bg-emerald-50 text-emerald-700 border-emerald-200/50"
                        : "bg-slate-50 text-slate-600 border-slate-200/50"
                    }`}>
                      <span className={`w-1.5 h-1.5 rounded-full ${status === "published" ? "bg-emerald-500" : "bg-slate-400"}`} />
                      {status}
                    </span>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <Globe size={14} className="text-slate-400 flex-shrink-0" />
                  <div>
                    <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Slug</p>
                    <p className="text-sm font-medium text-slate-700 break-all">{slug || "—"}</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Featured Image */}
            {imageUrl && (
              <div className="bg-white p-6 rounded-xl border border-slate-200 shadow-sm space-y-3">
                <h3 className="text-base font-bold text-teal-900">Featured Image</h3>
                <div className="aspect-video rounded-lg overflow-hidden border border-slate-200">
                  <img alt="Featured" className="w-full h-full object-cover" src={imageUrl} />
                </div>
              </div>
            )}

            {/* Taxonomy */}
            <div className="bg-white p-6 rounded-xl border border-slate-200 shadow-sm space-y-4">
              <h3 className="text-base font-bold text-teal-900">Taxonomy</h3>
              <div>
                <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-1">Category</p>
                <p className="text-sm font-medium text-slate-700">{categoryNameDisplay || "—"}</p>
              </div>
              {tags.length > 0 && (
                <div>
                  <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-2">Tags</p>
                  <div className="flex flex-wrap gap-2">
                    {tags.map((tag) => (
                      <span key={tag} className="inline-flex items-center gap-1 px-3 py-1 bg-teal-50 text-teal-800 rounded-full text-xs font-semibold">
                        <Tag size={10} />
                        {tag}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* SEO Preview */}
            <div className="hidden xl:block bg-white p-6 rounded-xl border border-slate-200 shadow-sm space-y-4">
              <h3 className="text-base font-bold text-teal-900">SEO Preview</h3>
              <div className="border border-slate-200 rounded-lg p-4 space-y-2">
                <p className="text-blue-700 text-lg hover:underline cursor-pointer line-clamp-1">
                  {computedSeoTitle}
                </p>
                <p className="text-green-700 text-xs">
                  zytama.com &gt; blogs &gt; {slug || "untitled-post"}
                </p>
                <p className="text-slate-500 text-sm line-clamp-3">
                  {computedSeoDescription}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Mobile Sticky Bottom Bar for View Actions */}
        <div className="fixed bottom-0 left-0 right-0 p-4 flex gap-3 z-30 sm:hidden animate-slideUp">
          {status === "published" ? (
            <button
              onClick={handleToggleStatus}
              disabled={isLoading}
              className="btn flex-1 justify-center py-3.5 rounded-xl font-bold text-sm bg-rose-50 border border-rose-200 text-rose-600 hover:bg-rose-100 transition-all disabled:opacity-50 cursor-pointer"
            >
              {isLoading ? (
                <div className="w-3.5 h-3.5 border-2 border-rose-400 border-t-transparent rounded-full animate-spin" />
              ) : (
                <Trash2 size={15} />
              )}
              Delete
            </button>
          ) : (
            <button
              onClick={handleToggleStatus}
              disabled={isLoading}
              className="btn flex-1 justify-center py-3.5 rounded-xl font-bold text-sm bg-emerald-50 border border-emerald-200 text-emerald-700 hover:bg-emerald-100 transition-all disabled:opacity-50 cursor-pointer"
            >
              {isLoading ? (
                <div className="w-3.5 h-3.5 border-2 border-emerald-500 border-t-transparent rounded-full animate-spin" />
              ) : (
                <CheckCircle2 size={15} />
              )}
              Publish
            </button>
          )}
          <button
            onClick={() => navigate(`/blogmanage/${id}?mode=edit`)}
            className="btn btn-p flex-1 justify-center py-3.5 rounded-xl font-bold text-sm cursor-pointer"
          >
            <Pencil size={15} /> Edit Post
          </button>
        </div>
      </div>
    );
  }

  // ── EDIT / CREATE MODE ─────────────────────────────────────────────────────
  return (
    <div className="p-4 max-w-[1400px] mx-auto space-y-6 pb-24 sm:pb-6">
      {error && (
        <div className="flex items-start gap-3 bg-red-50 border border-red-200 rounded-xl px-4 py-3">
          <span className="text-red-500 font-bold text-lg leading-none mt-0.5">!</span>
          <p className="text-sm text-red-700 font-medium">{error}</p>
        </div>
      )}

      {/* Editor Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center pb-4 border-b border-slate-200 gap-4">
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={handleCancel}
            className="p-2 border border-slate-200 rounded-xl text-slate-500 hover:bg-slate-50 transition-all active:scale-95 flex items-center justify-center cursor-pointer"
            title="Back to Blogs"
          >
            <ArrowLeft size={16} />
          </button>
          <div>
            <h2 className="text-xl font-extrabold text-slate-800 tracking-tight">
              {isNew ? (title ? `Creating: ${title}` : "Creating: Untitled Post") : (title || "Untitled Post")}
            </h2>
            <p className="text-xs text-slate-400 font-medium">Blog Editor Workspace</p>
          </div>
        </div>

        <div className="hidden sm:flex items-center gap-2 w-full sm:w-auto">
          <button
            onClick={handleCancel}
            disabled={isLoading}
            className="btn btn-g btn-sm flex items-center justify-center gap-1.5 disabled:opacity-50"
          >
            <X size={14} />
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={isLoading}
            className="btn btn-p btn-sm flex items-center justify-center gap-1.5 disabled:opacity-70 disabled:cursor-not-allowed"
          >
            {isLoading ? (
              <>
                <div className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                Saving...
              </>
            ) : (
              <>
                <Save size={14} />
                Save Post
              </>
            )}
          </button>
        </div>
      </div>

      {/* Editor Workspace */}
      <div className="grid grid-cols-1 xl:grid-cols-12 gap-6">
        {/* Primary Writing Area */}
        <div className="xl:col-span-8 space-y-6">
          <div className="bg-white p-6 rounded-xl border border-slate-200 shadow-sm focus-within:ring-2 focus-within:ring-teal-500/10 focus-within:border-teal-500 transition-all">
            <input
              type="text"
              placeholder="Enter your post title here..."
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              className="w-full text-3xl font-bold tracking-tight text-slate-800 border-none p-0 focus:ring-0 placeholder:text-slate-400 bg-transparent outline-none"
            />
          </div>

          <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden flex flex-col min-h-[600px] focus-within:ring-2 focus-within:ring-teal-500/10 focus-within:border-teal-500 transition-all">
            <div className="flex items-center flex-wrap gap-1 p-2 border-b border-slate-200 bg-slate-100">
              <button type="button" onMouseDown={(e) => { e.preventDefault(); handleFormat('bold'); }}
                className={`p-2 rounded transition-all cursor-pointer ${activeFormats.bold ? 'text-teal-700 bg-teal-100' : 'text-slate-500 hover:text-teal-900 hover:bg-slate-200/50'}`} title="Bold"><Bold size={15} /></button>
              <button type="button" onMouseDown={(e) => { e.preventDefault(); handleFormat('italic'); }}
                className={`p-2 rounded transition-all cursor-pointer ${activeFormats.italic ? 'text-teal-700 bg-teal-100' : 'text-slate-500 hover:text-teal-900 hover:bg-slate-200/50'}`} title="Italic"><Italic size={15} /></button>
              <button type="button" onMouseDown={(e) => { e.preventDefault(); handleFormat('underline'); }}
                className={`p-2 rounded transition-all cursor-pointer ${activeFormats.underline ? 'text-teal-700 bg-teal-100' : 'text-slate-500 hover:text-teal-900 hover:bg-slate-200/50'}`} title="Underline"><Underline size={15} /></button>
              <div className="w-[1px] h-6 bg-slate-200 mx-2" />
              <button type="button" onMouseDown={(e) => { e.preventDefault(); handleFormat('justifyLeft'); }}
                className={`p-2 rounded transition-all cursor-pointer ${activeFormats.justifyLeft ? 'text-teal-700 bg-teal-100' : 'text-slate-500 hover:text-teal-900 hover:bg-slate-200/50'}`} title="Align Left"><AlignLeft size={15} /></button>
              <button type="button" onMouseDown={(e) => { e.preventDefault(); handleFormat('justifyCenter'); }}
                className={`p-2 rounded transition-all cursor-pointer ${activeFormats.justifyCenter ? 'text-teal-700 bg-teal-100' : 'text-slate-500 hover:text-teal-900 hover:bg-slate-200/50'}`} title="Align Center"><AlignCenter size={15} /></button>
              <button type="button" onMouseDown={(e) => { e.preventDefault(); handleFormat('justifyRight'); }}
                className={`p-2 rounded transition-all cursor-pointer ${activeFormats.justifyRight ? 'text-teal-700 bg-teal-100' : 'text-slate-500 hover:text-teal-900 hover:bg-slate-200/50'}`} title="Align Right"><AlignRight size={15} /></button>
              <div className="w-[1px] h-6 bg-slate-200 mx-2" />
              <button type="button" onMouseDown={(e) => { e.preventDefault(); handleFormat('insertUnorderedList'); }}
                className={`p-2 rounded transition-all cursor-pointer ${activeFormats.insertUnorderedList ? 'text-teal-700 bg-teal-100' : 'text-slate-500 hover:text-teal-900 hover:bg-slate-200/50'}`} title="Bulleted List"><List size={15} /></button>
              <button type="button" onMouseDown={(e) => { e.preventDefault(); handleFormat('insertOrderedList'); }}
                className={`p-2 rounded transition-all cursor-pointer ${activeFormats.insertOrderedList ? 'text-teal-700 bg-teal-100' : 'text-slate-500 hover:text-teal-900 hover:bg-slate-200/50'}`} title="Numbered List"><ListOrdered size={15} /></button>
              <div className="w-[1px] h-6 bg-slate-200 mx-2" />
              <button type="button" onMouseDown={(e) => { e.preventDefault(); handleFormat('createLink'); }}
                className="p-2 rounded transition-all cursor-pointer text-slate-500 hover:text-teal-900 hover:bg-slate-200/50" title="Insert Link"><LinkIcon size={15} /></button>
              <button type="button" onMouseDown={(e) => { e.preventDefault(); handleFormat('insertImage'); }}
                className="p-2 rounded transition-all cursor-pointer text-slate-500 hover:text-teal-900 hover:bg-slate-200/50" title="Insert Image"><ImageIcon size={15} /></button>
              <button type="button" onMouseDown={(e) => { e.preventDefault(); handleFormat('insertCode'); }}
                className="p-2 rounded transition-all cursor-pointer text-slate-500 hover:text-teal-900 hover:bg-slate-200/50" title="Insert Code"><Code size={15} /></button>
              <div className="sm:ml-auto flex items-center gap-4 text-slate-400 text-xs px-4 font-semibold">
                <span>Words: {wordCount}</span>
                <span>Characters: {charCount}</span>
              </div>
            </div>
            <div
              ref={editorRef}
              contentEditable
              data-placeholder="Start writing your story..."
              onInput={(e) => setContent(e.currentTarget.innerHTML)}
              onKeyUp={updateActiveFormats}
              onMouseUp={updateActiveFormats}
              onSelect={updateActiveFormats}
              className="flex-1 p-6 outline-none overflow-y-auto text-base text-slate-700 leading-relaxed border-none focus:ring-0 min-h-[400px] bg-transparent empty:before:content-[attr(data-placeholder)] empty:before:text-slate-400 empty:before:pointer-events-none [&_ul]:list-disc [&_ul]:ml-4 [&_ol]:list-decimal [&_ol]:ml-4 [&_a]:text-teal-600 [&_a]:underline"
              suppressContentEditableWarning={true}
            />
          </div>
        </div>

        {/* Sidebar */}
        <div className="xl:col-span-4 space-y-6">
          {/* Post Settings */}
          <div className="bg-white p-6 rounded-xl border border-slate-200 shadow-sm space-y-4">
            <h3 className="text-base font-bold text-teal-900">Post Settings</h3>
            <div className="space-y-4">
              <div className="space-y-1">
                <label className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block">Author</label>
                <input
                  type="text"
                  placeholder="Enter author name"
                  value={author}
                  onChange={(e) => setAuthor(e.target.value)}
                  className="w-full bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 text-xs focus:ring-2 focus:ring-teal-700/20 outline-none"
                />
              </div>
              <div className="space-y-1">
                <label className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block">Blog Status</label>
                <select
                  value={status}
                  onChange={(e) => setStatus(e.target.value)}
                  className="w-full bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 text-xs focus:ring-2 focus:ring-teal-700/20 outline-none text-slate-800"
                >
                  <option value="published">Published</option>
                  <option value="unpublished">Unpublished</option>
                </select>
              </div>
              {/* <div className="space-y-1">
                <label className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block">Slug / URL Path</label>
                <input
                  type="text"
                  placeholder="post-url-slug"
                  value={slug}
                  onChange={(e) => setSlug(e.target.value)}
                  className="w-full bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 text-xs focus:ring-2 focus:ring-teal-700/20 outline-none"
                />
              </div> */}
            </div>
          </div>

          {/* Featured Image */}
          <div className="bg-white p-6 rounded-xl border border-slate-200 shadow-sm space-y-4">
            <div className="flex justify-between items-center">
              <h3 className="text-base font-bold text-teal-900">Featured Image</h3>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => {
                    const url = prompt("Enter cover image URL:", imageUrl);
                    if (url !== null) setImageUrl(url);
                  }}
                  className="text-[11px] font-semibold text-teal-700 hover:underline bg-transparent border-none cursor-pointer"
                >
                  Enter URL
                </button>
                {imageUrl && (
                  <>
                    <span className="text-slate-300 text-xs">|</span>
                    <button
                      type="button"
                      onClick={() => setImageUrl("")}
                      className="text-[11px] font-semibold text-red-600 hover:underline bg-transparent border-none cursor-pointer"
                    >
                      Remove
                    </button>
                  </>
                )}
              </div>
            </div>
            <input
              type="file"
              accept="image/*"
              id="featured-image-upload"
              className="hidden"
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) {
                  const reader = new FileReader();
                  reader.onloadend = () => setImageUrl(reader.result);
                  reader.readAsDataURL(file);
                }
              }}
            />
            <div
              onClick={() => document.getElementById("featured-image-upload").click()}
              className="aspect-video bg-slate-100 rounded-lg border-2 border-dashed border-slate-300 flex flex-col items-center justify-center cursor-pointer hover:bg-slate-200/50 transition-colors group relative overflow-hidden"
            >
              <img
                alt="Featured preview"
                className={`absolute inset-0 w-full h-full object-cover transition-opacity ${imageUrl ? "opacity-100" : "opacity-10 group-hover:opacity-20"}`}
                src={imageUrl || "https://images.unsplash.com/photo-1507238691740-187a5b1d37b8?auto=format&fit=crop&w=150&h=150&q=80"}
              />
              {!imageUrl && (
                <>
                  <ImageIcon className="text-slate-400 text-4xl mb-2 group-hover:scale-110 transition-transform" size={32} />
                  <p className="text-xs text-slate-400 font-medium">Click to select image file</p>
                </>
              )}
            </div>
          </div>

          {/* Taxonomy */}
          <div className="bg-white p-6 rounded-xl border border-slate-200 shadow-sm space-y-4">
            <h3 className="text-base font-bold text-teal-900">Taxonomy</h3>
            <div className="space-y-2">
              <label className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block">Primary Category</label>
              <select
                value={category}
                onChange={(e) => setCategory(e.target.value)}
                className="w-full bg-slate-100 border border-slate-200 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-teal-700/20 outline-none"
              >
                {categories.length === 0 ? (
                  <option value={category}>{categoryNameDisplay || "Select Category"}</option>
                ) : (
                  categories.map((cat) => (
                    <option key={cat.id || cat.categoryName} value={cat.id}>
                      {cat.categoryName}
                    </option>
                  ))
                )}
              </select>
            </div>
            <div className="space-y-2">
              <label className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block">Tags</label>
              <div className="flex flex-wrap gap-2 mb-3">
                {tags.map((tag) => (
                  <span key={tag} className="inline-flex items-center gap-1 px-3 py-1 bg-teal-50 text-teal-800 rounded-full text-xs font-semibold">
                    {tag}
                    <button
                      type="button"
                      onClick={() => handleRemoveTag(tag)}
                      className="hover:text-red-500 bg-transparent border-none p-0 cursor-pointer ml-1 text-xs"
                    >×</button>
                  </span>
                ))}
              </div>
              <div className="relative">
                <input
                  type="text"
                  placeholder="Add tag..."
                  value={tagInput}
                  onChange={(e) => setTagInput(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter") handleAddTag(e); }}
                  className="w-full bg-slate-100 border border-slate-200 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-teal-700/20 outline-none"
                />
                <button
                  type="button"
                  onClick={handleAddTag}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-teal-900 font-bold text-xl hover:text-teal-750 bg-transparent border-none cursor-pointer"
                >+</button>
              </div>
            </div>
          </div>

          {/* SEO Preview */}
          <div className="hidden xl:block bg-white p-6 rounded-xl border border-slate-200 shadow-sm space-y-4">
            <h3 className="text-base font-bold text-teal-900">SEO Preview</h3>
            <div className="border border-slate-200 rounded-lg p-4 space-y-2">
              <p className="text-blue-700 text-lg hover:underline cursor-pointer line-clamp-1">{computedSeoTitle}</p>
              <p className="text-green-700 text-xs">zytama.com &gt; blogs &gt; {slug || "untitled-post"}</p>
              <p className="text-slate-500 text-sm line-clamp-3">{computedSeoDescription}</p>
            </div>
            <button
              onClick={() => setShowSeoFields(!showSeoFields)}
              className="w-full py-2 text-teal-900 border border-teal-900/20 rounded-lg text-xs font-bold hover:bg-teal-900/5 transition-all cursor-pointer"
            >
              {showSeoFields ? "Hide Metadata Editor" : "Edit Metadata"}
            </button>
            {showSeoFields && (
              <div className="space-y-3 pt-2 border-t border-slate-100">
                <div>
                  <label className="block text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-1">Meta Title</label>
                  <input
                    type="text"
                    placeholder="Custom SEO title"
                    value={seoTitle}
                    onChange={(e) => setSeoTitle(e.target.value)}
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg text-xs focus:outline-none focus:ring-2 focus:ring-teal-700/20 outline-none bg-slate-50"
                  />
                </div>
                <div>
                  <label className="block text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-1">Meta Description</label>
                  <textarea
                    placeholder="Custom SEO description"
                    value={seoDescription}
                    onChange={(e) => setSeoDescription(e.target.value)}
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg text-xs focus:outline-none focus:ring-2 focus:ring-teal-700/20 outline-none h-20 resize-none bg-slate-50"
                  />
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Mobile Sticky Bottom Bar for Save/Cancel Actions */}
      <div className="fixed bottom-0 left-0 right-0 p-4 flex gap-3 z-30 sm:hidden animate-slideUp">
        <button
          onClick={handleCancel}
          disabled={isLoading}
          className="btn btn-g flex-1 justify-center py-3.5 rounded-xl font-bold text-sm cursor-pointer"
        >
          <X size={15} /> Cancel
        </button>
        <button
          onClick={handleSave}
          disabled={isLoading}
          className="btn btn-p flex-1 justify-center py-3.5 rounded-xl font-bold text-sm cursor-pointer"
        >
          {isLoading ? (
            <>
              <div className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin" />
              Saving...
            </>
          ) : (
            <>
              <Save size={15} /> Save Post
            </>
          )}
        </button>
      </div>
    </div>
  );
}
